import PropTypes from 'prop-types';
import { get, noop } from 'lodash';
import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import { Provider } from 'react-redux';
import styled, { css, ThemeProvider, withTheme } from 'styled-components';
import { grey25 } from '@zocdoc/branding/colors';
import { getLogo } from '@zocdoc/branding/brands/helpers';
import { filterToValidAttributes } from '@zocdoc/dom-property';
import { injectIntl, FormattedMessage } from 'react-intl';

import {
    UPDATE_PRIVACY_POLICY_BANNER_FLAG,
    UPDATE_PRIVACY_POLICY_BANNER_HIDDEN,
    UPDATE_PRIVACY_POLICY_BANNER_SHOWN,
} from '../../experiments';

import { breakpoint } from '../../grid';
import { Link } from '../../link';
import PrivacyUpdateBanner from '../../PrivacyUpdateBanner';
import { getLogoSizingCSSAtBreakpoint } from '../../themes/helpers';
import { FontFamily } from '../../typography';

import {
    HEADER_Y_AXIS_PADDING,
    LOGIN_STATE,
    PAGE_X_AXIS_PADDING_BY_BREAKPOINT,
    USER_TYPE,
} from '../constants';

import isMobileOrTabletBreakpoint from '../../utils/isMobileOrTabletBreakpoint';
import getLocalizedContent from '../../utils/getLocalizedContent';

import LockableLink from '../LockableLink';
import PaddedContainer from '../PaddedContainer';
import MobileMenu from './MobileMenu';
import PoweredByZocdoc from './PoweredByZocdocBadge';
import NotificationMenu from './NotificationMenu';
import UsernameDropdown from './UsernameDropdown';
import SignInDropdown from './SignInDropdown';
import {
    DesktopUserNavLinks,
    LOCALIZED_STRINGS,
    WellGuideLinkVariants,
} from './constants';
import StaticSmallBlobLogoImage from './assets/zocdoc-small-blob-logo.svg';
import { MetricsClickable, Metrics } from '@zocdoc/frontend-metrics';

class Header extends PureComponent {
    static propTypes = {
        ab: PropTypes.object,
        hideUserArea: PropTypes.bool, // hide content on the right side of the header

        // any React-renderable content
        mobileMenuContent: PropTypes.any,

        onCTALinkClick: PropTypes.func,
        onCustomLinkClick: PropTypes.func,
        onLogoClick: PropTypes.func,
        onMobileMenuLinkClick: PropTypes.func,
        onNavigationLinkClick: PropTypes.func,
        onNotificationDropdownClick: PropTypes.func,
        onNotificationClick: PropTypes.func,
        onSeeAllNotificationsClick: PropTypes.func,
        onSignInDropdownClick: PropTypes.func,
        onSignInDropdownLinkClick: PropTypes.func,
        onUpcomingAppointmentClick: PropTypes.func,
        onUsernameDropdownClick: PropTypes.func,
        onUsernameDropdownLinkClick: PropTypes.func,
        showNavigationLinks: PropTypes.bool,
        showUpcomingAppointmentsOnMobileMenu: PropTypes.bool,

        signInDropdownText: PropTypes.string,
        // created and injected in /src/Layout/Header/index.js
        store: PropTypes.object.isRequired,

        theme: PropTypes.shape({
            header: PropTypes.shape({
                ctaLink: PropTypes.shape({
                    uri: PropTypes.string,
                    text: PropTypes.string,
                    title: PropTypes.string,
                }),

                logo: PropTypes.shape({
                    linkUri: PropTypes.string,
                }),

                rightContent: PropTypes.arrayOf(
                    PropTypes.shape({
                        uri: PropTypes.string,
                        text: PropTypes.string,
                        title: PropTypes.string,
                    })
                ),
                showLoginDropdown: PropTypes.bool,
                showPoweredByZocdoc: PropTypes.bool,
                showPoweredByZocdocAsLink: PropTypes.bool,
            }),
        }),
        trackSignInLinkClickConversion: PropTypes.func,
        transparentOnDesktop: PropTypes.bool,
        useAnimatedLogo: PropTypes.bool,
        useStaticSmallBlobLogo: PropTypes.bool,
        useSignInDropdown: PropTypes.bool,
        user: PropTypes.object,
        wellGuideLinkVariant: PropTypes.string,
    };

    static defaultProps = {
        hideUserArea: false,
        onCTALinkClick: noop,
        onCustomLinkClick: noop,
        onLogoClick: noop,
        onMobileMenuLinkClick: noop,
        onNavigationLinkClick: ev => ev,
        onNotificationClick: noop,
        onNotificationDropdownClick: noop,
        onSeeAllNotificationsClick: noop,
        onUpcomingAppointmentClick: noop,
        onUsernameDropdownClick: noop,
        onUsernameDropdownLinkClick: noop,
        onSignInDropdownClick: noop,
        onSignInDropdownLinkClick: noop,
        trackSignInLinkClickConversion: noop,
        transparentOnDesktop: false,
        useAnimatedLogo: false,
        useSignInDropdown: false,
        user: {},
        wellGuideLinkVariant: WellGuideLinkVariants.OFF,
    };

    animationRendered = false;

    componentDidMount() {
        this.maybeRenderAnimation();
        window.addEventListener('resize', this.maybeRenderAnimation);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.maybeRenderAnimation);
    }

    render() {
        const {
            children,
            mobileMenuContent,
            onLogoClick,
            onMobileMenuLinkClick,
            showUpcomingAppointmentsOnMobileMenu,
            transparentOnDesktop,
            wellGuideLinkVariant,
            ...props
        } = this.props;

        return (
            <ThemeProvider theme={{ transparentOnDesktop }}>
                <Metrics section="PageHeader">
                    <div>
                        {this.maybeRenderPrivacyUpdateBanner()}
                        <StyledContainer
                            data-test="header"
                            data-uem-id="header"
                            {...filterToValidAttributes(props)}
                        >
                            <Provider store={this.props.store}>
                                <div>
                                    {this.maybeRenderPoweredByZocdoc()}
                                    <OurHeaderContent>
                                        <StyledMobileMenu
                                            data-test="header-mobile-menu"
                                            onBeforeLinkClick={onMobileMenuLinkClick}
                                            showUpcomingAppointmentsOnMobileMenu={showUpcomingAppointmentsOnMobileMenu}
                                            wellGuideLinkVariant={wellGuideLinkVariant}
                                        >
                                            {mobileMenuContent}
                                        </StyledMobileMenu>

                                        <Metrics
                                            action="Click PageHeader Logo"
                                            component="Logo"
                                            element="Logo"
                                        >
                                            {this.renderLogo()}
                                        </Metrics>

                                        {this.maybeRenderUserArea()}
                                    </OurHeaderContent>
                                </div>
                            </Provider>
                            {children}
                        </StyledContainer>
                        <BottomBorder />
                    </div>
                </Metrics>
            </ThemeProvider>
        );
    }


    renderCustomContent = (definition, index) => {
        if (definition.uri) {
            let metricsData = {};

            const isSignIn = definition.uri.indexOf('signin') !== -1;
            const isJoin = definition.uri.indexOf('join') !== -1;

            if (isSignIn) {
                metricsData = {
                    action: 'Click Page Header Sign In Link',
                    element: 'SignInLink',
                };
            } else if (isJoin) {
                metricsData = {
                    action: 'Click Page Header Join Link',
                    element: 'JoinLink',
                };
            }

            return (
                <MetricsClickable
                    {...metricsData}
                    key={index}
                >
                    <StyledLink
                        key={index}
                        data-test="header-custom-content-link"
                        href={definition.uri}
                        onClick={this.props.onCustomLinkClick}
                        title={definition.title}
                    >
                        {definition.text}
                    </StyledLink>
                </MetricsClickable>
            );
        } else {
            return (
                <span
                    key={index}
                    data-test="header-custom-content-text"
                >
                    {definition.text}
                </span>
            );
        }
    }

    maybeRenderCustomContent(showLoginDropdown, content) {
        const {
            onSignInDropdownClick,
            onSignInDropdownLinkClick,
            useSignInDropdown,
            trackSignInLinkClickConversion,
        } = this.props;

        if (useSignInDropdown && showLoginDropdown) {
            return (
                <SignInDropdownContainer data-test="signin-dropdown-container">
                    <SignInDropdown
                        displayName={this.getDropDownText()}
                        onSignInDropdownClick={onSignInDropdownClick}
                        onSignInDropdownLinkClick={onSignInDropdownLinkClick}
                        trackSignInLinkClickConversion={trackSignInLinkClickConversion}
                    />
                </SignInDropdownContainer>
            );
        }

        if (content) {
            return (
                <Metrics component="RightContentCustomLink">
                    <StyledLinkGroup data-test="header-custom-content">
                        {content.map(this.renderCustomContent)}
                    </StyledLinkGroup>
                </Metrics>
            );
        }
    }

    getDropDownText() {
        const {
            signInDropdownText,
            intl,
        } = this.props;

        return signInDropdownText || intl.formatMessage(LOCALIZED_STRINGS.LoginSignup);
    }

    maybeRenderCTA = ctaLinkDefinition => {
        if (ctaLinkDefinition) {
            return (
                <StyledCTALinkGroup>
                    <StyledCTALinkContainer>
                        <MetricsClickable
                            action="Click PageHeader CTA Link"
                            component="CTALink"
                            element="ListYourPractice"
                        >
                            <StyledCTALink
                                beforeClick={this.props.onCTALinkClick}
                                data-test="header-cta"
                                href={ctaLinkDefinition.uri}
                                title={ctaLinkDefinition.title}
                            >
                                {ctaLinkDefinition.text}
                            </StyledCTALink>
                        </MetricsClickable>
                    </StyledCTALinkContainer>
                </StyledCTALinkGroup>
            );
        }
    }

    maybeRenderPoweredByZocdoc() {
        const {
            theme: { header },
            user,
        } = this.props;

        const loginState = this.getLoginState(user);
        if (header.showPoweredByZocdoc && loginState === LOGIN_STATE.LoggedOut) {
            return (
                <StyledPoweredByZocdoc
                    component={header.showPoweredByZocdocAsLink ? 'a' : undefined}
                    data-test="header-powered_by_zocdoc"
                />
            );
        }
    }

    getLoginState(user) {
        return get(user, 'login_state', LOGIN_STATE.LoggedOut);
    }

    maybeRenderUserArea() {
        const {
            hideUserArea,
            onUsernameDropdownClick,
            onUsernameDropdownLinkClick,
            showNavigationLinks,
            theme: { header },
            theme,
            intl,
            useAnimatedLogo,
            user,
        } = this.props;

        if (hideUserArea) {
            return null;
        }

        const loginState = this.getLoginState(user);
        if (loginState === LOGIN_STATE.LoggedOut) {
            const ctaLink = getLocalizedContent({
                intl,
                theme,
                path: 'header.ctaLink',
            });

            const rightContent = getLocalizedContent({
                intl,
                theme,
                path: 'header.rightContent',
            });

            return (
                <CtaContent data-test="cta-content">
                    {this.maybeRenderCTA(ctaLink)}
                    {this.maybeRenderCustomContent(header.showLoginDropdown, rightContent)}
                </CtaContent>

            );
        } else if (loginState === LOGIN_STATE.SoftLoggedIn || loginState === LOGIN_STATE.LoggedIn) {
            const displayName = user.display_name || user.email;
            return (
                <Metrics component="RightContentCustomLink">
                    <UserHeader
                        data-test="user-menu"
                        useAnimatedLogo={useAnimatedLogo}
                    >
                        {this.maybeRenderNavigationLinks(loginState)}
                        {this.maybeRenderNotificationMenu()}

                        <UsernameDropdown
                            displayName={displayName}
                            onUsernameBarClick={onUsernameDropdownClick}
                            onUsernameDropdownLinkClick={onUsernameDropdownLinkClick}
                            usePatientNavigationLinks={showNavigationLinks}
                            userType={user.user_type}
                        />
                    </UserHeader>
                </Metrics>
            );
        }
    }

    maybeRenderNavigationLinks(userLoginState) {
        const {
            user,
        } = this.props;

        if (user.user_type === USER_TYPE.Patient) {
            return this.maybeRenderPatientNavigationLinks(userLoginState);
        } else {
            return this.maybeRenderProviderNavigationLinks(userLoginState);
        }
    }

    shouldRenderPatientNavigationLinks(userLoginState) {
        const {
            showNavigationLinks,
        } = this.props;

        return showNavigationLinks
            && (userLoginState === LOGIN_STATE.LoggedIn || userLoginState === LOGIN_STATE.SoftLoggedIn);
    }

    maybeRenderPatientNavigationLinks(userLoginState) {
        if (this.shouldRenderPatientNavigationLinks(userLoginState)) {
            return this.renderNavigationLinks(DesktopUserNavLinks.patient);
        }
    }

    maybeRenderProviderNavigationLinks(userLoginState) {
        if (userLoginState === LOGIN_STATE.LoggedIn) {
            return this.renderNavigationLinks(DesktopUserNavLinks.Provider);
        }
    }

    renderNavigationLinks(userNavLinks) {
        return (
            <NavLinkGroup data-test="header-nav-group">
                {userNavLinks.map(navLink => {
                    return this.renderNavigationLink(navLink, this.props.onNavigationLinkClick);
                })}
            </NavLinkGroup>
        );
    }

    renderNavigationLink(navLink, onNavigationLinkClick) {
        const key = navLink.dataTest;

        return (
            <NavLinkDiv
                key={key}
            >
                <StyledLink
                    beforeClick={ev => onNavigationLinkClick(ev, key)}
                    data-test={`header-nav-links-${key}`}
                    data-uem-id={`header-nav-links-${key}`}
                    href={navLink.url}
                >
                    <FormattedMessage {...navLink.message} />
                </StyledLink>
            </NavLinkDiv>
        );
    }

    maybeRenderNotificationMenu() {
        const {
            onNotificationClick,
            onNotificationDropdownClick,
            onSeeAllNotificationsClick,
            onUpcomingAppointmentClick,
            user,
        } = this.props;

        if (user.user_type === USER_TYPE.Patient) {
            return (
                <NotificationMenu
                    data-test="header-notification-menu"
                    onNotificationClick={onNotificationClick}
                    onNotificationDropdownClick={onNotificationDropdownClick}
                    onSeeAllNotificationsClick={onSeeAllNotificationsClick}
                    onUpcomingAppointmentClick={onUpcomingAppointmentClick}
                />
            );
        }
    }

    maybeRenderPrivacyUpdateBanner() {
        const { ab } = this.props;
        const featureFlag = get(
            ab,
            UPDATE_PRIVACY_POLICY_BANNER_FLAG.experimentId,
            UPDATE_PRIVACY_POLICY_BANNER_HIDDEN
        );

        if (featureFlag === UPDATE_PRIVACY_POLICY_BANNER_SHOWN) {
            return <PrivacyUpdateBanner />;
        }
    }

    renderLogo() {
        const {
            onLogoClick,
            theme: { header },
            transparentOnDesktop,
            useAnimatedLogo,
            useStaticSmallBlobLogo,
        } = this.props;

        if (useStaticSmallBlobLogo) {
            return this.renderStaticSmallBlobLogo();
        } else {
            const LogoComponent = transparentOnDesktop ? TransparentStaticLogo : StaticLogo;

            return (
                <Logo>
                    <MetricsClickable>
                        {useAnimatedLogo && (
                            <AnimatedLogo
                                beforeClick={onLogoClick}
                                data-test="animated-header-logo"
                                href={header.logo.linkUri}
                                innerRef={node => { this.$animatedLogoContainer = findDOMNode(node); }}
                                title="Back to homepage"
                            />
                        )}

                        <LogoComponent
                            beforeClick={onLogoClick}
                            data-test="static-header-logo"
                            data-uem-id="static-header-logo"
                            href={header.logo.linkUri}
                            title="Back to homepage"
                        />
                    </MetricsClickable>
                </Logo>
            );
        }
    }

    renderStaticSmallBlobLogo() {
        const {
            onLogoClick,
            theme: { header },
        } = this.props;
        return (
            <Logo>
                <MetricsClickable>
                    <StaticSmallBlobLogo
                        beforeClick={onLogoClick}
                        data-test="static-small-blob-header-logo"
                        data-uem-id="static-small-blob-header-logo"
                        href={header.logo.linkUri}
                        title="Back to homepage"
                    />
                </MetricsClickable>
            </Logo>
        );
    }

    maybeRenderAnimation = () => {
        if (
            this.props.useAnimatedLogo
            && !this.animationRendered
            && process.env.NODE_ENV !== 'test'
            && !isMobileOrTabletBreakpoint()
        ) {
            this.renderAnimation();
        }
    };

    async renderAnimation() {
        this.animationRendered = true;

        const [lottie, json] = await Promise.all([
            import(/* webpackChunkName: "header-animation" */'lottie-web/build/player/lottie_light.min.js'),
            import(/* webpackChunkName: "header-animation" */'./AnimatedLogo.json'),
        ]);

        const animationParams = {
            container: this.$animatedLogoContainer,
            renderer: 'svg',
            loop: false,
            autoplay: true,
            animationData: json,
        };

        // enzyme cannot run with real dom manipulations from lottie
        lottie.loadAnimation(animationParams);
    }
}

/* region Styles */

const themeHeader = props => props.theme.header;

const hideForMobile = css`
    ${breakpoint('small', { down: true })} {
        display: none;
    }
`;

const StyledMobileMenu = styled(MobileMenu)`
    z-index: 10;
`;

const BottomBorder = styled.div`
    background: ${p => p.theme.header.bottomBorderColor};
    display: ${p => p.theme.header.bottomBorderColor === 'transparent' ? 'none' : 'block'};
    height: 1px;

    ${breakpoint.medium} {
        background: ${p => p.theme.transparentOnDesktop ? 'transparent' : p.theme.header.bottomBorderColor};
    }
`;

const StyledContainer = styled(PaddedContainer)`
    background: ${p => p.theme.header.bgColor};
    box-sizing: border-box;
    color: ${p => themeHeader(p).textColor};
    font-family: ${FontFamily.SharpSansSemibold};
    font-size: 14px;
    padding-bottom: ${HEADER_Y_AXIS_PADDING}px;
    padding-top: ${HEADER_Y_AXIS_PADDING}px;
    position: relative;

    *, *::before, *::after {
        box-sizing: border-box;
    }

    ${breakpoint.medium} {
        background: ${p => p.theme.transparentOnDesktop ? 'none' : p.theme.header.bgColor};
    }

`;

const OurHeaderContent = styled.div`
    position: relative;
    align-items: center;
    display: flex;
`;

const CtaContent = styled.div`
    align-self: flex-start;
    align-items: center;
    display: flex;
    ${breakpoint.medium} {
        align-self: ${p => themeHeader(p).showPoweredByZocdoc ? 'flex-end' : 'flex-start'};

    }
`;

const UserHeader = styled.div`
    align-self: ${p => p.useAnimatedLogo ? 'flex-start' : 'inherit'};

    display: none;
    ${breakpoint.medium} {
        display: block;
    }
`;

const Logo = styled.div`
    margin-right: auto;
`;

const StaticLogo = styled(LockableLink)`
    background-image: url("${p => getLogo(p.theme).uri}");
    background-position: center left;
    background-repeat: no-repeat;
    background-size: contain;
    display: block;

    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'xxsmall')}
    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'xsmall')}
    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'small')}
    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'medium')}
    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'large')}
    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'xlarge')}
    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'xxlarge')}
`;

const TransparentStaticLogo = styled(StaticLogo)`
    ${breakpoint.medium} {
        background-image: url("${p => getLogo(p.theme).transparentUri || getLogo(p.theme).uri}");
    }
`;

const AnimatedLogo = styled(Link)`
    border: none !important;
    display: none;
    position: absolute;
    top: 0;
    left: 0;

    ${breakpoint.medium} {
        display: block;

        & ~ ${StaticLogo},
        & ~ ${TransparentStaticLogo} {
            display: none;
        }
    }
`;

const StaticSmallBlobLogo = styled(LockableLink)`
    background-image: url(${StaticSmallBlobLogoImage});
    background-position: center left;
    background-repeat: no-repeat;
    background-size: contain;
    display: block;
    width: 154px;
    height: 44px;
`;

const StyledPoweredByZocdoc = styled(PoweredByZocdoc)`
    border: 1px solid ${grey25};
    border-top: 0;
    color: ${p => themeHeader(p).textColor};
    padding: 7px 10px;
    width: 120px;
    position: absolute;
    top: 0;
    right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXSMALL}px;

    ${breakpoint('small', { down: true })} {
        display: none;
    }

    ${breakpoint.small} {
        right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.SMALL}px;
    }

    ${breakpoint.medium} {
        right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.MEDIUM}px;
    }

    ${breakpoint.large} {
        right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.LARGE}px;
    }

    ${breakpoint.xlarge} {
        right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XLARGE}px;
    }

    ${breakpoint.xxlarge} {
        right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXLARGE}px;
    }
`;

const StyledLink = styled(Link)`
    border-bottom-color: ${p => themeHeader(p).linkUnderlineColor};
    color: ${p => themeHeader(p).linkColor};
    display: inline-block;
    &:focus,
    &:hover {
        border-bottom-color: ${p => themeHeader(p).linkUnderlineColorHover};
        color: ${p => themeHeader(p).linkColorHover};
    }
    &:visited {
        border-bottom-color: ${p => themeHeader(p).linkUnderlineColorVisited};
        color: ${p => themeHeader(p).linkColorVisited};
    }
`;

const StyledCTALink = styled(Link)`
    color: ${p => themeHeader(p).linkColor};
    font-size: 14px;
    display: inline-block;
    ${hideForMobile}
`;
const StyledCTALinkContainer = styled.div`
    border-right: 1px solid ${p => themeHeader(p).ctaLinkColor};
    padding: 0 20px;

    ${hideForMobile}
`;
const StyledCTALinkGroup = styled.div`
    ${breakpoint.medium} {
        padding: 13px 0 12px 0;
    }
`;
const StyledLinkGroup = styled.div`
    margin-left: 21px;

    ${hideForMobile}

    > *:not(:first-child) {
        margin-left: 4px;
    }
`;

const NavLinkGroup = styled.div`
    display: inline-flex;
    flex-wrap: nowrap;
    margin: 10px 10px;
`;

const NavLinkDiv = styled.div`
    margin-right: 25px;
`;

const SignInDropdownContainer = styled.div`
    margin-left: 20px;
    ${hideForMobile}
`;

/* endregion Styles */

const HeaderWithIntl = injectIntl(Header);
export default withTheme(HeaderWithIntl);
export const primitives = {
    AnimatedLogo,
    BottomBorder,
    Container: StyledContainer,
    CtaContent,
    CTALink: StyledCTALink,
    Link: StyledLink,
    LinkGroup: StyledLinkGroup,
    Logo,
    MobileMenu: StyledMobileMenu,
    PoweredByZocdoc: StyledPoweredByZocdoc,
    StaticLogo,
    TransparentStaticLogo,
    UserHeader,
    ZDHeaderContent: OurHeaderContent,
};
